import { Box, Card, Skeleton, Stack, Typography, useTheme } from "@mui/material";

import psLogo from "../../../../assets/fsk8s/perfect-scale.png";
import psLogoDark from "../../../../assets/fsk8s/perfect-scale-dark.png";
import { useDarkThemeCheck } from "../../../../Components/hooks/useDarkThemeCheck";
import { CircularProgressWithLabel } from "../../../../Components/ProgressWithLabel";
import InfoIconWithTooltip from "../../Components/InfoIconWithTooltip";
import { useCustomerHasPerfectScale } from "../../hooks";
import { K8S_LENS_GLOSSARY } from "../../messages";
import PerfectScaleLink from "../../Upsell/Components/PerfectScaleLink";
import { useClusterDashboardContext } from "./Table/context";

const Fsk8sBanner = () => {
  const theme = useTheme();
  const isDarkMode = useDarkThemeCheck();
  const { dashboardClusters30d, clusters30dLoading: loading } = useClusterDashboardContext();
  const customerHasPerfectScale = useCustomerHasPerfectScale();

  const { potentialSavings, cost } = dashboardClusters30d.summary || {};
  const potentialSavingsPercent = potentialSavings && cost ? Math.round((potentialSavings / cost) * 100) : 0;

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.general.backgroundDark,
        width: "100%",
        p: 4,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        borderRadius: 1,
        minHeight: "100%",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        flexDirection={{ xs: "column", lg: "row" }}
        sx={{ gap: { xs: 2, lg: 0 } }}
      >
        <img
          src={isDarkMode ? psLogoDark : psLogo}
          alt="PerfectScale by DoiT"
          height={51}
          style={{ marginBottom: 8 }}
        />

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ flexDirection: { xs: "column", lg: "row" }, alignItems: { xs: "center", lg: "center" } }}
        >
          <Box>
            {loading ? (
              <Skeleton variant="circular" height={48} width={48} />
            ) : (
              <CircularProgressWithLabel
                size={48}
                value={potentialSavingsPercent}
                backgroundColor={theme.palette.statusWarning[isDarkMode ? "dark" : "light"]}
                color="warning"
                typographyProps={{
                  variant: "h4",
                  fontWeight: 500,
                  color: "textPrimary",
                }}
              />
            )}
          </Box>
          <Stack alignItems={{ xs: "center", lg: "flex-start" }} textAlign={{ xs: "center", lg: "left" }}>
            <Typography variant="subtitle1">Savings potential</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Based on your current usage
              </Typography>
              <InfoIconWithTooltip tooltip={K8S_LENS_GLOSSARY.SAVINGS_POTENTIAL} />
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <Stack spacing={3} alignItems="center" justifyContent="center" flexGrow={1}>
        <Stack spacing={1} textAlign="center">
          <Typography variant="h3">
            {customerHasPerfectScale
              ? "Put your Kubernetes optimization on auto-pilot"
              : "Get a 30 day free trial of PerfectScale"}
          </Typography>
          {customerHasPerfectScale ? (
            <Typography variant="body1">
              Go to the PerfectScale platform to ensure peak performance with data-driven, autonomous actions that
              continuously optimize each layer of your K8s stack for comprehensive cost efficiency.
            </Typography>
          ) : (
            <Typography variant="body1">
              Gain full visibility, smarter recommendations, and cost-saving opportunities—built for DevOps teams who
              want more control, better efficiency, and actionable data.
            </Typography>
          )}
        </Stack>
        <Box display="flex" justifyContent="center" width="100%">
          <PerfectScaleLink content={(hasPS) => (hasPS ? undefined : "Start your trial")} />
        </Box>
      </Stack>
    </Card>
  );
};

export default Fsk8sBanner;
